import { Suspense, useEffect, useMemo } from "react";
import { Routes, useLocation } from "react-router-dom";
import { ErrorBoundary } from "@/components/error-boundary";
import { Loader } from "@/components/loader/loader";
import { QueryErrorHandler } from "@/components/query-error-handler";
import { ROLES } from "@/constants/constants";
import { usePermission } from "@/hooks/usePermissions";
import { useIsI18nEnabled } from "@/i18n/feature-flag";
import { stripLeadingLocale } from "@/router/use-localized-href";
import { reloadUserpilot } from "@/services/userpilot";
import SidebarStatusProvider from "@/store/sidebar-status-context";
import { ToastProvider } from "../../components/toast/toast-provider";
import { Layout } from "../../layout/Layout";
import { LoaderWrapper } from "./main.components";
import { useMainAppSetup } from "./main.hooks";
import type { MainProps } from "./main.types";
import {
	generateDeviceFingerprintRoutes,
	generateFullSubscriptionRoutes,
} from "./main.utils";

/**
 * Main application component that handles routing based on user subscriptions
 * and feature flags. It initializes the user context and renders appropriate
 * routes based on the user's subscription level.
 */
export const Main = ({ userData }: MainProps) => {
	const location = useLocation();
	const i18nEnabled = useIsI18nEnabled();
	const routesLocation = useMemo(() => {
		if (!i18nEnabled) return location;
		const stripped = stripLeadingLocale(location.pathname);
		return stripped === location.pathname
			? location
			: { ...location, pathname: stripped };
	}, [location, i18nEnabled]);

	const { hasAccess: hasAdminAccess } = usePermission({ role: ROLES.ADMIN });
	const {
		subscriptions,
		hasDeviceFingerprintOnly,
		defaultRoute,
		flagList,
		isLoading,
	} = useMainAppSetup(userData);

	useEffect(() => {
		if (["/events"].includes(location.pathname)) {
			reloadUserpilot();
		}
	}, [location.pathname]);

	// Wait for subscriptions to load before rendering routes
	// This prevents route flashing and incorrect redirects
	if (subscriptions.length === 0) {
		return null;
	}

	// Generate routes based on subscription type
	const routes = hasDeviceFingerprintOnly
		? generateDeviceFingerprintRoutes(hasAdminAccess, isLoading)
		: generateFullSubscriptionRoutes(
				hasAdminAccess,
				flagList,
				subscriptions,
				defaultRoute,
				isLoading,
			);

	return (
		<ToastProvider>
			<QueryErrorHandler />
			<SidebarStatusProvider>
				<Layout>
					<ErrorBoundary key={location.pathname} showSidebar>
						<Suspense
							fallback={
								<LoaderWrapper>
									<Loader size="50" />
								</LoaderWrapper>
							}
						>
							<Routes location={routesLocation}>{routes}</Routes>
						</Suspense>
					</ErrorBoundary>
				</Layout>
			</SidebarStatusProvider>
		</ToastProvider>
	);
};

export default Main;
